import React from 'react';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Types
import { AiSuggestionWorkspaceItem } from 'Shared/types/AiSuggestionWorkspaceItem.type';

// Utils
import { handleLaunchExternalLink } from 'Shared/utils/external-link';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpText } from 'Wpds/components/text';
import { WpButton } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import { WpBadge } from 'Wpds/components/badge';
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Styles
import './AgendaAiItem.scss';

type Props = {
  suggestion: AiSuggestionWorkspaceItem;
  handleAcceptAiSuggestion: Function;
};

const AgendaAiItem = ({ suggestion, handleAcceptAiSuggestion }: Props) => {
  const [isAccepting, toggleIsAccepting] = useToggle(false);
  const [hasAdded, toggleHasAdded] = useToggle(false);

  const handleAcceptClick = () => {
    if (isAccepting) return;
    toggleIsAccepting();
    handleAcceptAiSuggestion({ aiSuggestionSlug: suggestion.slug }).then(() => {
      toggleIsAccepting();
      toggleHasAdded();
      WpToastManager.success('Topic created');
    });
  };

  const handleClickBadge = () => {
    const link = suggestion?.message.slackLink;
    if (link) handleLaunchExternalLink({ href: link });
  };

  const baseClassName = 'AgendaAiListItem';

  return (
    <WpContainer color="gray" className={baseClassName}>
      <WpContainerBody>
        <div className={`${baseClassName}__body`}>
          <div className={`${baseClassName}__body__details`}>
            <WpText noMargin>
              {suggestion.body} {suggestion.additionalInformation ? ` - ${suggestion.additionalInformation}` : ''}
            </WpText>
            <div className={`${baseClassName}__body__details__meta`}>
              <div
                onClick={handleClickBadge}
                onKeyDown={handleClickBadge}
                role="button"
                tabIndex={0}
                className={`${baseClassName}__body__channel`}
              >
                <WpBadge color="blue" size="sm">
                  <WpIcon name="hashtag" size="sm" />
                  {suggestion.message.channelName}
                </WpBadge>
              </div>
            </div>
          </div>
          <div>
            {hasAdded ? (
              <div className={`${baseClassName}__body__added`}>
                <WpIcon name="check-circle" prefix="fas" color={ColorConstants.purple[800]} />
                Added
              </div>
            ) : (
              <WpTooltip>
                <WpTooltipTrigger>
                  <WpButton variant="secondary" size="sm" onClick={handleAcceptClick} loading={isAccepting}>
                    <WpIcon name="plus" />
                  </WpButton>
                </WpTooltipTrigger>
                <WpTooltipContent>Add to agenda</WpTooltipContent>
              </WpTooltip>
            )}
          </div>
        </div>
      </WpContainerBody>
    </WpContainer>
  );
};

export default AgendaAiItem;
